import { Text } from '@kk/ui';
import type { ReactNode } from 'react';

import { kaLogoWhiteUrl, kkWordmarkWhiteUrl } from '@/assets';
import { deck } from '@/copy/deck';
import { isAdminHost } from '@/lib/host';

/**
 * **THE SIGNED-OUT FRAME, and it says whose portal this is.**
 *
 * Before this the sign-in screen was one card floating in empty space with nothing on it but a button — a
 * person who followed a link had no way to tell what they had arrived at, or whether it was meant for them.
 *
 * **EVERY signed-out state uses this same frame** (Evan, 2026-09-22: *"Same shell for both states, so moving
 * from one to the other doesn't jump the layout"*). Signing in, being refused for not being staff, and a
 * sign-in that broke are three answers to one question, and a person often sees two of them in a row; a
 * layout that jumps between them reads as a different site rather than a different answer.
 *
 * Desktop is two panels at 45/55 from 1019px; below that it is one column with the turquoise as a band, so the
 * card is the first thing in reach on a phone rather than something to scroll to.
 */
export function SignInShell({ children }: { children: ReactNode }) {
  // O00c: every signed-out state on admin.kiddiekredit.com carries the admin's panel, not the clubs one
  const admin = isAdminHost();
  return (
    <div className="signin-frame">
      <aside className="signin-panel">
        <div className="signin-panel-top">
          {/*
           * The wordmark alone, white knockout: there is no white version of the rounded kk tile, so the
           * lockup a club director knows is the words. Delivered artwork, placed and never retyped.
           */}
          <img
            className="signin-wordmark"
            src={kkWordmarkWhiteUrl}
            alt={deck.shell.kiddieKredit}
            data-testid="signin-wordmark"
          />
          {admin ? (
            <>
              {/* O00c: the admin's own label and headline, and no card stack */}
              <p className="signin-panel-label" data-testid="signin-panel-label">
                {deck.signIn.admin.panelLabel}
              </p>
              <h1 className="signin-headline" data-testid="signin-headline">
                {deck.signIn.admin.panelHeadline}
              </h1>
            </>
          ) : (
            <>
              <h1 className="signin-headline" data-testid="signin-headline">
                {deck.signIn.panelHeadline}
              </h1>
              <p className="signin-panel-body">{deck.signIn.panelBody}</p>
            </>
          )}
        </div>
        {/*
         * The endorsement, same rule as the club shell's footer: two words typeset, and the Kredit Academy
         * LOCKUP standing for the other two. A lockup retyped in HTML is a different mark.
         */}
        <div className="signin-endorsement" data-testid="signin-powered-by">
          <Text variant="chip" tone="white">
            {deck.shell.poweredBy}
          </Text>
          <img src={kaLogoWhiteUrl} alt={deck.shell.kreditAcademy} />
        </div>
      </aside>
      <main className="signin-stage">
        <div className="signin-card" data-testid="signin-card">
          {children}
        </div>
      </main>
    </div>
  );
}
